/* eslint-disable array-callback-return */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { css, StyleSheet } from 'aphrodite/no-important';
import { connect } from 'react-redux';
import {
  Toast,
  TabsContainer,
  TabItem,
  PopupMenu,
  TextInput,
  Button,
  DialogBox,
  Checkbox,
  TimePicker
} from 'quantux-ui';
import CaseDoc from '../case-doc';
import CaseTab from '../case-schedule/case-tab';
import PatientTab from '../case-schedule/patient-tab';
import PayerTab from '../case-schedule/payer-tab';
import CaseField from '../revenue-cycle-management/case-field';
import CaseUserCard from '../revenue-cycle-management/case-user-card';
import PatientCard from '../patient-card';
import IntraOp from '../case-schedule/intraop';
import PreOp from '../case-schedule/preop';
import {
  updateCaseBlockActiveCaseSchedule,
  markCaseForUpdate,
  addPatientToCaseBlock,
  setShouldSaveCaseData
} from '../../actions';
import { blue, gray, salmon } from '../../theme';
import { FLAG_VALUE, BASE_URL, findFlag, padZero, getHighlightedText, parseDateFromIsoString, debugLogToConsole } from '../../utils';
import CaseSuccessIcon from '../svg-icons/case-success-icon';
import CaseWarningIcon from '../svg-icons/case-warning-icon';
import CaseCriticalIcon from '../svg-icons/case-critical-icon';
import DocumentNoneIcon from '../svg-icons/document-none-icon';
import DocumentSuccessIcon from '../svg-icons/document-success-icon';
import DocumentCriticalIcon from '../svg-icons/document-critical-icon';
import DocumentWarningIcon from '../svg-icons/document-warning-icon';

// #region ICONS
const IS_NONE_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none"><path fill="#96C8FF" fillRule="evenodd" d="M8 0C3.584 0 0 3.584 0 8s3.584 8 8 8 8-3.584 8-8-3.584-8-8-8ZM1.714 8.029c0-3.452 2.855-6.248 6.379-6.248 1.475 0 2.83.492 3.907 1.32l-8.938 8.754A6.091 6.091 0 0 1 1.714 8.03ZM4 12.904a6.385 6.385 0 0 0 3.907 1.32c3.524 0 6.379-2.796 6.379-6.247a6.091 6.091 0 0 0-1.348-3.827L4 12.904Z" clipRule="evenodd" /></svg>
);

const CRITIAL_ICON = (
  <svg
    width="16"
    height="13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16 12.364 8 0 0 12.364h16Zm-7.273-2.182H7.273V8.728h1.454v1.454ZM7.273 8h1.454V5.091H7.273v2.91Z"
      fill="#FF0"
    />
  </svg>
);

const BELL_ICON = (
  <svg
    width="16"
    height="16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14 7.439v4.376l2 1.75v.875H9.988C9.878 15.314 9.026 16 8 16c-1.036 0-1.88-.686-1.988-1.56H0v-.875l2-1.75V7.439c0-2.696 1.63-4.936 4.5-5.531v-.595C6.5.586 7.17 0 8 0s1.5.586 1.5 1.313v.595c2.86.595 4.5 2.844 4.5 5.53Z"
      fill="#FF6060"
    />
  </svg>
);

const SUCCESS_ICON = (
  <svg
    width="12"
    height="12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 6a6 6 0 1 1 12 0A6 6 0 1 1 0 6Zm2.646-.238 1.896 1.796L9.36 3l.641.608-5.458 5.17L2 6.369l.646-.607Z"
      fill="#00B080"
    />
  </svg>
);
// #endregion

const MOBILE_BREAK_POINT = 1012; // was 821 before
const DRAWER_WIDTH = 355;
const DIALOG_WIDTH = window.innerWidth <= 576 ? (window.innerWidth - 16) : 350;
const COMMON_TAB_ITEM_STYLES = {
  height: '30px',
  borderBottom: 'none',
  marginRight: '0'
};

const TABS = [
  {
    id: 'faceSheet_tab',
    pKey: '1111',
    isIconText: false,
    text: 'FaceSheet',
    hideOnMobile: false,
    styles: { ...COMMON_TAB_ITEM_STYLES, width: window.innerWidth > 720 ? '16%' : '26%', minWidth: '90px', maxWidth: window.innerWidth > 720 ? '16%' : '26%' }
  },
  {
    id: 'records_tab',
    pKey: '1331',
    isIconText: false,
    text: 'Records',
    hideOnMobile: false,
    styles: { ...COMMON_TAB_ITEM_STYLES, width: window.innerWidth > 720 ? '16%' : '20%', minWidth: '90px', maxWidth: window.innerWidth > 720 ? '16%' : '20%' }
  },
  {
    id: 'consents_tab',
    pKey: '1112',
    isIconText: false,
    text: 'Consents',
    hideOnMobile: false,
    styles: { ...COMMON_TAB_ITEM_STYLES, width: window.innerWidth > 720 ? '16%' : '25%', minWidth: '90px', maxWidth: window.innerWidth > 720 ? '16%' : '25%', boderLeft: '1px solid black' }
  },
  {
    id: 'nursing_tab',
    pKey: '1113',
    isIconText: false,
    text: 'Nursing',
    hideOnMobile: true,
    styles: { ...COMMON_TAB_ITEM_STYLES, width: '16%', minWidth: '90px', maxWidth: '16%', borderLeft: '1px solid black' }
  },
  {
    id: 'surgeon_tab',
    pKey: '1114',
    isIconText: false,
    text: 'Surgeon',
    hideOnMobile: true,
    styles: { ...COMMON_TAB_ITEM_STYLES, width: '16%', minWidth: '90px', maxWidth: '16%', borderLeft: '1px solid black' }
  },
  {
    id: 'anesthesia_tab',
    pKey: '1115',
    isIconText: false,
    text: 'Anesthesia',
    hideOnMobile: false,
    styles: { ...COMMON_TAB_ITEM_STYLES, width: window.innerWidth > 720 ? '16%' : '29%', minWidth: '90px', maxWidth: window.innerWidth > 720 ? '16%' : '29%', borderLeft: '1px solid black' }
  },
  {
    id: 'orders_tab',
    pKey: '8899',
    isIconText: false,
    text: 'Orders',
    hideOnMobile: true,
    styles: { ...COMMON_TAB_ITEM_STYLES, width: '16%', minWidth: '90px', maxWidth: '16%', borderLeft: '1px solid black' }
  },
  {
    id: 'discharge_tab',
    pKey: '1116',
    isIconText: false,
    text: 'Discharge',
    hideOnMobile: true,
    styles: { ...COMMON_TAB_ITEM_STYLES, width: '16%', minWidth: '90px', maxWidth: '16%', borderLeft: '1px solid black' }
  }
];

const TABS_TEXT_STYLES = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '12px'
};

const CbScheduleDashboard = (props) => {
  const [showSavedMsg, setShowSavedMsg] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [user, setUser] = useState();
  const [masterFlag, setMasterFlag] = useState(FLAG_VALUE.critical);
  const [scheduleMasterFlag, setScheduleMasterFlag] = useState(FLAG_VALUE.critical);
  const [isMobileLayout, setIsMobileLayout] = useState(window.innerWidth < MOBILE_BREAK_POINT);
  const [showScheduleDrawer, setShowScheduleDrawer] = useState(false);

  // --- Case Tab Data
  const [caseData, setCaseData] = useState({});

  // --- Case Docs Tabs
  const [activeTab, setActiveTab] = useState(TABS[0].text);
  const [consentActiveTab, setConsentActiveTab] = useState('anesthesia');
  const [anesthesiaSubTab, setAnesthesiaSubTab] = useState('preOp');
  const [recordsSubTab, setRecordsSubTab] = useState('labs');
  const [ordersSubTab, setOrdersSubTab] = useState('preOp');

  // --- Patient Card
  const [patientDos, setPatientDos] = useState('');
  const [patientAdmType, setPatientAdmType] = useState('');
  const [patientFacility, setPatientFacility] = useState('');
  const [patientSurgeons, setPatientSurgeons] = useState([]);
  const [patientAnesthesia, setPatientAnesthesia] = useState([]);
  const [patientPreOpDx, setPatientPreOpDx] = useState([]);
  const [patientSurgicalProcedures, setPatientSurgicalProcedures] = useState([]);

  // --- Schedule Forms Drawer
  const [drawerActiveTab, setDrawerActiveTab] = useState('case');
  const [caseTabIcon, setCaseTabIcon] = useState(CRITIAL_ICON);
  const [caseFlag, setCaseFlag] = useState(FLAG_VALUE.critical);
  const [patientFlag, setPatientFlag] = useState(FLAG_VALUE.warning);
  const [payerFlag, setPayerFlag] = useState(FLAG_VALUE.warning);

  // Patient handover
  const [patientHandover, setPatientHandover] = useState('PACU');

  // --- Patient Tab & Data
  const [patientData, setPatientData] = useState({});

  // -- Case Docs (NEW)
  const [faceSheetDocs, setFaceSheetDocs] = useState([]);
  const [isNoneFaceSheet, setIsNoneFaceSheet] = useState(false);
  // Consent -- Anesthesia
  const [consentFormDocs, setConsentFormDocs] = useState([]);
  const [isNoneConsentFormDocs, setIsNoneConsentFormDocs] = useState(false);
  // Consent - Surgery
  const [consentSurgFormDocs, setConsentSurgFormDocs] = useState([]);
  const [isNoneConsentSurgFormDocs, setIsNoneConsentSurgFormDocs] = useState(true);
  // -- Anesthesia sub tabs
  const [preOpFormDocs, setPreOpFormDocs] = useState([]);
  const [isNonePreOpFormDocs, setIsNonePreOpFormDocs] = useState(false);
  const [intraOpFormDocs, setIntraOpFormDocs] = useState([]);
  const [isNoneIntraOpFormDocs, setIsNoneIntraOpFormDocs] = useState(false);
  const [postOpFormDocs, setPostOpFormDocs] = useState([]);
  const [isNonePostOpFormDocs, setIsNonePostOpFormDocs] = useState(false);
  // --- New Docs
  // Anesthesia Procedures
  const [anesthesiaProcedureValue, setAnesthesiaProcedureValue] = useState('');
  const [showAnesthesiaProceduresSearchMenu, setShowAnesthesiaProceduresSearchMenu] = useState(false);
  const [anesthesiaProceduresSearchResults, setAnesthesiaProceduresSearchResults] = useState([]);
  const [anesthesiaProcedures, setAnesthesiaProcedures] = useState([]);
  const [isNoneAnesthesiaProcedure, setIsNoneAnesthesiaProcedure] = useState(false);
  // Records -- Lab
  const [labsDocs, setLabsDocs] = useState([]);
  const [isNoneLabsDocs, setIsNoneLabsDocs] = useState(true);
  // Records -- EKG
  const [ekgDocs, setEkgDocs] = useState([]);
  const [isNoneEkgDocs, setIsNoneEkgDocs] = useState(true);
  // Records -- Imaging
  const [imagingDocs, setImagingDocs] = useState([]);
  const [isNoneImagingDocs, setIsNoneImagingDocs] = useState(true);
  // Records -- Consults
  const [consultsDocs, setConsultsDocs] = useState([]);
  const [isNoneConsults, setIsNoneConsults] = useState(true);
  // Nursing
  const [isNoneNursingDocs, setIsNoneNursingDocs] = useState(true);
  const [nursingDocs, setNursingDocs] = useState([]);
  // Surgeon
  const [isNoneSurgeonDocs, setIsNoneSurgeonDocs] = useState(true);
  const [surgeonDocs, setSurgeonDocs] = useState([]);
  // Orders - PreOp Form Docs
  const [ordersPreOpDocs, setOrdersPreOpDocs] = useState([]);
  const [isNoneOrdersPreOpDocs, setIsNoneOrdersPreOpDocs] = useState(true);
  // Orders - IntraOp Form Docs
  const [ordersIntraOpDocs, setOrdersIntraOpDocs] = useState([]);
  const [isNoneOrdersIntraOpDocs, setIsNoneOrdersIntraOpDocs] = useState(true);
  // Orders - Post Op Form Docs
  const [ordresPostOpDocs, setOrdresPostOpDocs] = useState([]);
  const [isNoneOrdersPostOpDocs, setIsNoneOrdersPostOpDocs] = useState(true);
  // Discharge
  const [dischargeDocs, setDischargeDocs] = useState([]);
  const [isNoneDischargeDocs, setIsNoneDischargeDocs] = useState(true);
  // -- Disccard | Post Case | Post Case
  const [showDisacardDialog1, setShowDiscardDialog1] = useState(false);
  const [showDisacardDialog2, setShowDiscardDialog2] = useState(false);
  const [shouldSaveCase, setShouldSaveCase] = useState(false);

  /* ---------------- Case times ----------------  */
  // Anes Start Time
  const [anesStartHour, setAnesStartHour] = useState(24);
  const [anesStartMinute, setAnesStartMinute] = useState(60);
  const [isAnesStartTimePristine, setIsAnesStartTimePristine] = useState(true);
  // Anes Stop Time
  const [anesStopHour, setAnesStopHour] = useState(24);
  const [anesStopMinute, setAnesStopMinute] = useState(60);
  const [isAnesStopTimePristine, setIsAnesStopTimePristine] = useState(true);
  const [showAnesStopFlag, setShowAnesStopFlag] = useState(true);

  // --- Anesthesia Data
  // -- ASA Score
  const [asaScore, setAsaScore] = useState('');
  // -- Anesthesia Types
  const [anesthesiaTypes, setAnesthesiaTypes] = useState([]);
  // Anesthesia Modifiers
  const [anesthesiaModifiers, setAnesthesiaModifiers] = useState([]);
  // Window height
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  /* ___ REFS ________________________________________________________ */
  const procedureSearchMenuRef = useRef();
  const headerMenuRef = useRef();

  /* ___ INITIAL DATA LOAD ___________________________________________ */
  const loadCaseScheduleData = () => {
    const { scheduleStartDate, asa, asaScore } = props.caseData;

    // -- Date of Surgery
    const { year: dosYear, month: dosMonth, date: dosDate } = parseDateFromIsoString(scheduleStartDate);
    if (!props.caseData.isDosTbd) {
      setPatientDos(`${padZero(+dosYear)}-${padZero(+dosMonth + 1)}-${padZero(+dosDate)}T00:00:00.000Z`);
    } else {
      setPatientDos('');
    }

    if (props.caseData.patientType && props.caseData.patientType.length > 0) {
      setPatientAdmType(props.caseData.patientType);
    } else {
      setPatientAdmType('Not Set');
    }

    // Anesthesia procedures
    if (props.caseData.anesthesiaProcedures && Array.isArray(props.caseData.anesthesiaProcedures)) {
      setAnesthesiaProcedures(props.caseData.anesthesiaProcedures.concat(props.caseData.otherAnesthesiaProcedures));
    }
    setIsNoneAnesthesiaProcedure(props.caseData.isNoneAnesthesiaProcedure || false);
    // FaceSheet docs
    setFaceSheetDocs(props.caseData.faceSheetDocs);
    setIsNoneFaceSheet(props.caseData.isNoneFaceSheet);
    // Recoards Docs
    setLabsDocs(props.caseData.labsDocs);
    setIsNoneLabsDocs(props.caseData.isNoneLabDocs);
    setEkgDocs(props.caseData.ekgDocs);
    setIsNoneEkgDocs(props.caseData.isNoneEkgDocs);
    setImagingDocs(props.caseData.imagingDocs);
    setIsNoneImagingDocs(props.caseData.isNoneImagingDocs);
    setConsultsDocs(props.caseData.consultsDocs);
    setIsNoneConsults(props.caseData.isNoneConsults);
    // Consents docs
    setConsentFormDocs(props.caseData.consentFormDocs);
    setIsNoneConsentFormDocs(props.caseData.isNoneConsentFormDocs);
    setConsentSurgFormDocs(props.caseData.consentSurgFormDocs);
    setIsNoneConsentSurgFormDocs(props.caseData.isNoneConsentSurgFormDocs);
    // Nursing
    setNursingDocs(props.caseData.nursingDocs);
    setIsNoneNursingDocs(props.caseData.isNoneNursingDocs);
    // Surgeon Docs
    setSurgeonDocs(props.caseData.surgeonDocs);
    setIsNoneSurgeonDocs(props.caseData.isNoneSurgeonDocs);
    // Anestehsia Docs
    setPreOpFormDocs(props.caseData.preOpFormDocs);
    setIsNonePreOpFormDocs(props.caseData.isNonePreOpFormDocs)
    setIntraOpFormDocs(props.caseData.intraOpFormDocs);
    setIsNoneIntraOpFormDocs(props.caseData.isNoneIntraOpFormDocs)
    setPostOpFormDocs(props.caseData.postOpFormDocs);
    setIsNonePostOpFormDocs(props.caseData.isNonePostOpFormDocs);
    setIsNoneAnesthesiaProcedure(props.caseData.isNoneAnesthesiaProcedure);
    // Orders Docs
    setOrdersPreOpDocs(props.caseData.ordersPreOpDocs);
    setIsNoneOrdersPreOpDocs(props.caseData.isNoneOrdersPreOpDocs);
    setOrdersIntraOpDocs(props.caseData.ordersIntraOpDocs);
    setIsNoneOrdersIntraOpDocs(props.caseData.isNoneOrdersIntraOpDocs);
    setOrdresPostOpDocs(props.caseData.ordresPostOpDocs);
    setIsNoneOrdersPostOpDocs(props.caseData.isNoneOrdersPostOpDocs);
    // Dischange Docs
    setDischargeDocs(props.caseData.dischargeDocs);
    setIsNoneDischargeDocs(props.caseData.isNoneDischargeDocs);

    // -- Anesthesia Procedures
    setAnesthesiaProcedureValue();

    // Anesthesia Interop Data
    if (asa) {
      setAsaScore(asa);
    } else if (asaScore) {
      setAsaScore(asaScore);
    }
    setAnesthesiaTypes(props.caseData.anesthesiaTypes || []);
    setAnesthesiaModifiers(props.caseData.anesthesiaModifiers || []);

    // Times
    if (props.caseData.anesthesiaStartHour !== undefined && props.caseData.anesthesiaStartMinute !== undefined) {
      setAnesStartHour(props.caseData.anesthesiaStartHour);
      setAnesStartMinute(props.caseData.anesthesiaStartMinute);
      setIsAnesStartTimePristine(false);
    } else {
      setAnesStartHour(24);
      setAnesStartMinute(60);
    }

    if (props.caseData.anesthesiaEndHour !== undefined && props.caseData.anesthesiaEndMinute !== undefined) {
      setAnesStopHour(props.caseData.anesthesiaEndHour);
      setAnesStopMinute(props.caseData.anesthesiaEndMinute);
      setIsAnesStopTimePristine(false);
    } else {
      setAnesStopHour(24);
      setAnesStopMinute(60);
    }

    // --- Case data
    // console.log('🔥🔥 ___________ 🔥🔥');
    // console.log(props.caseData);
    setCaseData(props.caseData);
    // if (props.patientData) {
    //   setPatientData(props.patientData);
    //   setPatientFlag(Object.keys(props.patientData).length > 0 ? FLAG_VALUE.success : FLAG_VALUE.warning);
    // }
  };

  /* ___ WINDOW EVENTS _______________________________________________ */
  const handleWindowResize = () => {
    // setIsMobileLayout(window.innerWidth < MOBILE_BREAK_POINT);
    setWindowHeight(window.innerHeight);
  };
  const debouncedHandleWindowResize = useCallback(debounce(() => handleWindowResize(), 800), []);

  /* ___ EFFECTS _____________________________________________________ */
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();

    const usr = localStorage.getItem('usr');
    if (usr) {
      setUser(JSON.parse(usr));
    }

    if (Object.keys(caseData).length === 0) {
      loadCaseScheduleData();
    }

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  // --- Payer Flag
  useEffect(() => {
    let piFlag = FLAG_VALUE.warning;
    let siFlag = FLAG_VALUE.warning;
    let tiFlag = FLAG_VALUE.warning;
    let spFlag = FLAG_VALUE.warning;

    // Primary Insurance Flag
    if (patientData.isNoPrimaryInsurance) {
      piFlag = FLAG_VALUE.success;
    } else {
      if (patientData.primaryInsurance && Object.keys(patientData.primaryInsurance).length > 0) {
        piFlag = FLAG_VALUE.success;
      } else {
        piFlag = FLAG_VALUE.warning;
      }
    }

    // Secondary Insurance Flag
    if (patientData.isNoSecondaryInsurance) {
      siFlag = FLAG_VALUE.success;
    } else {
      if (patientData.secondaryInsurance && Object.keys(patientData.secondaryInsurance).length > 0) {
        siFlag = FLAG_VALUE.success;
      } else {
        siFlag = FLAG_VALUE.warning;
      }
    }

    // Tertiary Insurance Flag
    if (patientData.isNoTertiaryInsurance) {
      tiFlag = FLAG_VALUE.success;
    } else {
      if (patientData.tertiaryInsurance && Object.keys(patientData.tertiaryInsurance).length > 0) {
        tiFlag = FLAG_VALUE.success;
      } else {
        tiFlag = FLAG_VALUE.warning;
      }
    }

    // Self Pay Flag
    if (patientData.isNoSelfPay) {
      spFlag = FLAG_VALUE.success;
    } else {
      spFlag = FLAG_VALUE.warning;
    }

    setPayerFlag(findFlag(piFlag, siFlag, tiFlag, spFlag));
  }, [patientData]);

  // Search Anesthesia Procedure Value
  // --- Surgical Procedures Search ---
  const searchSurgicalProcedures = (value) => {
    const url = new URL(`${BASE_URL}/anesProc/searchAnesthesiaProcedures`);
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setShowAnesthesiaProceduresSearchMenu(true);
        setAnesthesiaProceduresSearchResults(data.anesthesiaProcedures);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedProceduresSearch = useCallback(debounce(value => searchSurgicalProcedures(value), 800), []);

  // Window resize
  useEffect(() => {
    window.addEventListener('resize', debouncedHandleWindowResize);
    window.addEventListener('orientationchange', debouncedHandleWindowResize);
    return () => {
      window.removeEventListener('resize', debouncedHandleWindowResize);
      window.removeEventListener('orientationchange', debouncedHandleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (anesthesiaProcedureValue && anesthesiaProcedureValue.length === 0) {
      setAnesthesiaProceduresSearchResults([]);
    }
    if (anesthesiaProcedureValue && anesthesiaProcedureValue.trim().length > 0) {
      debouncedProceduresSearch(anesthesiaProcedureValue);
    }
  }, [anesthesiaProcedureValue]);

  // Schedule drawer ** Master Flag **
  useEffect(() => {
    setMasterFlag(findFlag(props.caseTabFlag, patientFlag, payerFlag));
  }, [
    props.caseTabFlag,
    patientFlag,
    payerFlag
  ]);

  // Case Times Flags ( For Each Step)
  useEffect(() => {
    const anesStartTime = `${anesStartMinute < 10 ? anesStartHour * 10 : anesStartHour}${anesStartMinute}`;
    const anesStopTime = `${anesStopMinute < 10 ? anesStopHour * 10 : anesStopHour}${anesStopMinute}`;
    console.log(`🩻 anesStartTime = ${anesStartTime} -- ${typeof anesStartHour}, ${typeof anesStartMinute}`);
    console.log(`🩻 anesStopTime = ${anesStopTime} -- ${typeof anesStopHour}, ${typeof anesStopMinute}`);
    const tmp = +anesStopTime > +anesStartTime;
    console.log(`Value of anesTopFlag = ${tmp}`);
    setShowAnesStopFlag(+anesStopTime <= +anesStartTime);
  }, [
    anesStartHour,
    anesStartMinute,
    anesStopHour,
    anesStopMinute
  ]);

  // Schedule Master Flag
  useEffect(() => {
    /* ----- Case Times ----- */
    // const timeFlag = showAnesStopFlag ? FLAG_VALUE.critical : FLAG_VALUE.success;
    const timeFlag = (isAnesStopTimePristine || showAnesStopFlag) ? FLAG_VALUE.critical : FLAG_VALUE.success;

    /* ----- Face Sheet Tab Flag ----- */
    let faceSheetFlag = FLAG_VALUE.critical;
    if (isNoneFaceSheet) {
      faceSheetFlag = FLAG_VALUE.none;
    } else if (faceSheetDocs.length > 0 || isNoneFaceSheet) {
      faceSheetFlag = FLAG_VALUE.success;
    } else {
      faceSheetFlag = FLAG_VALUE.critical;
    }

    /* ----- Records Tab Flag ----- */
    let recordsFlag = FLAG_VALUE.critical;
    if (isNoneLabsDocs && isNoneEkgDocs && isNoneImagingDocs && isNoneConsults) {
      recordsFlag = FLAG_VALUE.none;
    } else {
      const labsFlag = labsDocs.length > 0 || isNoneLabsDocs;
      const ekgFlag = ekgDocs.length > 0 || isNoneEkgDocs;
      const imagingFlag = imagingDocs.length > 0 || isNoneImagingDocs;
      const consultsFlag = consultsDocs.length > 0 || isNoneConsults;

      if (labsFlag && ekgFlag && imagingFlag && consultsFlag) {
        recordsFlag = FLAG_VALUE.success;
      } else {
        recordsFlag = FLAG_VALUE.critical;
      }
    }

    /* ----- Consent Tab Flag ----- */
    let consentFlag = FLAG_VALUE.critical;
    if (isNoneConsentSurgFormDocs && isNoneConsentFormDocs) {
      consentFlag = FLAG_VALUE.none;
    } else {
      const surgFlag = consentSurgFormDocs.length > 0 || isNoneConsentSurgFormDocs;
      const anesFlag = consentFormDocs.length > 0 || isNoneConsentFormDocs;

      if (surgFlag && anesFlag) {
        consentFlag = FLAG_VALUE.success;
      } else {
        consentFlag = FLAG_VALUE.critical;
      }
    }

    /* ----- Anesthesia Tab Flag ----- */
    let anesthesiaFlag = FLAG_VALUE.critical;
    // PreOp Tab Flag
    let preOpFlagValue = FLAG_VALUE.critical;
    if (isNonePreOpFormDocs) {
      preOpFlagValue = FLAG_VALUE.none;
    } else {
      if (preOpFormDocs.length > 0) {
        preOpFlagValue = FLAG_VALUE.success;
      } else {
        preOpFlagValue = FLAG_VALUE.critical;
      }
    }

    // IntraOp Tab Flag
    let intraOpFlagValue = FLAG_VALUE.critical;
    let anesthesiaDataFlagValue = FLAG_VALUE.critical;
    if (asaScore.length > 0 && anesthesiaTypes.length > 0 && anesthesiaModifiers.length > 0) {
      anesthesiaDataFlagValue = FLAG_VALUE.success;
    }
    if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
      intraOpFlagValue = anesthesiaDataFlagValue;
    } else {
      if (isNoneIntraOpFormDocs) {
        intraOpFlagValue = FLAG_VALUE.none;
      } else {
        if (intraOpFormDocs.length > 0) {
          intraOpFlagValue = FLAG_VALUE.success;
        } else {
          intraOpFlagValue = FLAG_VALUE.critical;
        }
      }
    }

    // PostOp Tab Flag
    let postOpFlagValue = FLAG_VALUE.critical;
    if (isNonePostOpFormDocs) {
      postOpFlagValue = FLAG_VALUE.none;
    } else {
      if (postOpFormDocs.length > 0) {
        postOpFlagValue = FLAG_VALUE.success;
      } else {
        postOpFlagValue = FLAG_VALUE.critical;
      }
    }

    // Procedures Tab Flag
    let proceduresFlagValue = FLAG_VALUE.critical;
    if (isNoneAnesthesiaProcedure) {
      proceduresFlagValue = FLAG_VALUE.none;
    } else {
      if (anesthesiaProcedures.length === 0) {
        proceduresFlagValue = FLAG_VALUE.critical;
      } else if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
        proceduresFlagValue = FLAG_VALUE.warning;
      } else if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length === 0) {
        proceduresFlagValue = FLAG_VALUE.success;
      } else {
        proceduresFlagValue = FLAG_VALUE.critical;
      }
    }

    const anesthesiaTabFlag = findFlag(preOpFlagValue, intraOpFlagValue, postOpFlagValue, proceduresFlagValue);
    switch (anesthesiaTabFlag) {
      case FLAG_VALUE.critical:
        anesthesiaFlag = FLAG_VALUE.critical;
        break;
      case FLAG_VALUE.warning:
        anesthesiaFlag = FLAG_VALUE.warning;
        break;
      case FLAG_VALUE.success:
        anesthesiaFlag = FLAG_VALUE.success;
        break;
      case FLAG_VALUE.none:
        anesthesiaFlag = FLAG_VALUE.none;
        break;
      default:
        anesthesiaFlag = FLAG_VALUE.critical;
    }

    /* ----- Nursing Tab Flag ----- */
    let nursingFlag = FLAG_VALUE.critical;
    if (isNoneNursingDocs) {
      nursingFlag = FLAG_VALUE.none;
    } else {
      if (nursingDocs.length > 0 || isNoneNursingDocs) {
        nursingFlag = FLAG_VALUE.success;
      } else {
        nursingFlag = FLAG_VALUE.critical;
      }
    }

    /* ----- Discharge Tab Flag ----- */
    let dcFlag = FLAG_VALUE.critical;
    if (isNoneDischargeDocs) {
      dcFlag = FLAG_VALUE.none;
    } else {
      if (dischargeDocs.length > 0 || isNoneDischargeDocs) {
        dcFlag = FLAG_VALUE.success;
      }
      dcFlag = FLAG_VALUE.critical;
    }

    /* ----- Orders Tab Flag ----- */
    let ordersFlag = FLAG_VALUE.critical;
    if (isNoneOrdersPreOpDocs && isNoneOrdersIntraOpDocs && isNoneOrdersPostOpDocs) {
      ordersFlag = FLAG_VALUE.none;
    }
    const preOPFlag = ordersPreOpDocs.length > 0 || isNoneOrdersPreOpDocs;
    const intraOpFlag = ordersIntraOpDocs.length > 0 || isNoneOrdersIntraOpDocs;
    const postOpFlag = ordresPostOpDocs.length > 0 || isNoneOrdersPostOpDocs;

    if (preOPFlag && intraOpFlag && postOpFlag) {
      ordersFlag = FLAG_VALUE.success;
    } else {
      ordersFlag = FLAG_VALUE.critical;
    }

    // Setting master scheudle flag
    setScheduleMasterFlag(findFlag(
      masterFlag,
      timeFlag,
      faceSheetFlag,
      recordsFlag,
      consentFlag,
      anesthesiaFlag,
      nursingFlag,
      dcFlag,
      ordersFlag
    ));
  }, [
    masterFlag,
    isNoneFaceSheet,
    faceSheetDocs,
    labsDocs,
    isNoneLabsDocs,
    ekgDocs,
    isNoneEkgDocs,
    imagingDocs,
    isNoneImagingDocs,
    consultsDocs,
    isNoneConsults,
    consentSurgFormDocs,
    isNoneConsentSurgFormDocs,
    consentFormDocs,
    isNoneConsentFormDocs,
    preOpFormDocs,
    isNonePreOpFormDocs,
    asaScore,
    anesthesiaTypes,
    anesthesiaModifiers,
    isNoneIntraOpFormDocs,
    intraOpFormDocs,
    postOpFormDocs,
    isNonePostOpFormDocs,
    isNoneAnesthesiaProcedure,
    anesthesiaProcedures,
    nursingDocs,
    isNoneNursingDocs,
    surgeonDocs,
    isNoneSurgeonDocs,
    dischargeDocs,
    isNoneDischargeDocs,
    isNoneOrdersPreOpDocs,
    ordersPreOpDocs,
    isNoneOrdersIntraOpDocs,
    ordersIntraOpDocs,
    isNoneOrdersPostOpDocs,
    ordresPostOpDocs,
    showAnesStopFlag,
    isAnesStopTimePristine
  ]);

  // Face Sheet Docs
  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ faceSheetDocs });
  }, [faceSheetDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ labsDocs });
  }, [labsDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ ekgDocs });
  }, [ekgDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ imagingDocs });
  }, [imagingDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ consultsDocs });
  }, [consultsDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ consentSurgFormDocs });
  }, [consentSurgFormDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ consentFormDocs });
  }, [consentFormDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ nursingDocs });
  }, [nursingDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ surgeonDocs });
  }, [surgeonDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ preOpFormDocs });
  }, [preOpFormDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ intraOpFormDocs });
  }, [intraOpFormDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ postOpFormDocs });
  }, [postOpFormDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ anesthesiaProcedures });
  }, [anesthesiaProcedures]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ ordersPreOpDocs });
  }, [ordersPreOpDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ ordersIntraOpDocs });
  }, [ordersIntraOpDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ ordresPostOpDocs });
  }, [ordresPostOpDocs]);

  useEffect(() => {
    props.updateCaseBlockActiveCaseSchedule({ dischargeDocs });
  }, [dischargeDocs]);

  useEffect(() => {
    if (props.caseBlockPatients && Object.keys(props.caseBlockPatients).length > 0) {
      setPatientFlag(FLAG_VALUE.success);
      const { patientId } = props.caseBlockActiveCaseSchedule;
      if (props.caseBlockPatients[patientId] && Object.keys(props.caseBlockPatients[patientId]).length > 0) {
        setPatientData(props.caseBlockPatients[patientId]);
      }
    } else {
      setPatientFlag(FLAG_VALUE.warning);
    }
  }, [props.caseBlockPatients]);

  /* ___ STYLES ______________________________________________________ */
  const styles = StyleSheet.create({
    dashboardContainer: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: blue[900],
      zIndex: '5000',
      overflow: 'hidden'
    },
    header: {
      height: '40px',
      background: '#004080',
      borderBottom: '1px solid #000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 4px'
    },
    headerText: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      fontWeight: 'bold',
      lineHeight: '16px'
    },
    sectionsContainer: {
      // height: 'calc(100% - 40px)',
      // height: 'calc(100vh - 40px)',
      // height: `${window.innerHeight - 40}px`,
      height: `${windowHeight - 40}px`,
      display: 'flex',
      overflow: 'hidden'
    },
    tabsContainer: {
      position: 'relative',
      display: 'flex',
      flexFlow: 'column',
      // flexGrow: '1'
      width: showScheduleDrawer ? 'calc(100vw - 355px)' : '100vw',
      maxWidth: showScheduleDrawer ? 'calc(100vw - 355px)' : '100vw'
    },
    scheduleFormContainerMobile: {
      top: '40px',
      right: '0',
      height: 'calc(100vh - 40px)'
      // top: '0px',
      // height: '100vh'
      // boxShadow: '-10px 2px 4px rgba(0, 160, 255, .25)'
    },
    scheduleFormContainer: {
      // transition: 'width 0.1s linear',
      width: '355px',
      borderLeft: `${showScheduleDrawer ? '1px' : '0'} solid ${blue[400]}`,
      background: gray[700],
      position: isMobileLayout ? 'absolute' : 'relative',
      top: '0px'
      // position: 'absolute',
      // right: '0px',
      // height: '100vh',
      // overflowY: 'auto'
    },
    scheduleFormContainerClosed: {
      width: '0',
      display: isMobileLayout ? 'none' : ''
    },
    scheduleFormHeader: {
      display: 'flex',
      alignItems: 'center',
      height: '40px',
      backgroundColor: blue[700],
      padding: '0 8px'
    },
    scheduleFormHeaderTitle: {
      flexGrow: '1'
    },
    scheduleFormHeaderCloseIcon: {
      flexGrow: '0',
      ':hover': {
        color: salmon[500]
      }
    },
    patientStickerContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingRight: '4px',
      height: '141px',
      minHeight: '141px',
      background: blue[900],
      flexGrow: '0'
    },
    docTabs: {
      background: 'black',
      padding: '1px 0',
      flexGrow: '0'
    },
    docContainer: {
      height: 'calc(100% - 185px)'
    },
    pdfDocContainer: {
      width: '100%',
      // height: '100%'
      height: 'calc(100% - 32px)'
    },
    scheduleDrawerTabs: {
      maxHeight: 'calc(100% - 56px)',
      overflowY: 'auto',
      overflowX: 'hidden'
    },
    activeComponentContainer: {
      height: 'calc(100% - 71px)',
      overflowY: 'auto'
    },
    menuItem: {
      cursor: 'pointer',
      background: gray[900],
      color: gray[300],
      padding: '4px',
      lineHeight: '16px',
      fontSize: '14px',
      marginTop: '1px',
      ':hover': {
        background: '#184878',
        color: 'white'
      },
      ':last-child': {
        borderBottom: '0'
      }
    },
    smallMenuItem: {
      height: '40px',
      background: blue[700],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      cursor: 'pointer',
      ':hover': {
        color: blue[800],
        background: blue[300]
      }
    },
    searchPopupMenuContainer: {
      width: `${DRAWER_WIDTH - 21}px`,
      background: blue[900],
      paddingBottom: '1px',
      borderRadius: '5px',
      overflowY: 'auto',
      maxHeight: '480px'
    },
    ellipsisBtn: {
      position: 'relative',
      width: '24px',
      height: '24px',
      background: blue[200],
      color: gray[600],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '2px'
    }
  });

  /* ___ EVENT HANDLERS ______________________________________________ */
  // #region ** Case | Patient | Payers Tabs *** Event Handlers

  const handleOnCaseDataChange = () => {
    if (!shouldSaveCase) {
      setShouldSaveCase(true);
    }
  };

  const handleOnChangePatientFlag = (value) => {
    setPatientFlag(value);
  };
  // #endregion

  // #region ** Patient Card ** Event Handlers
  const openCasePatientDrawer = () => {
    setShowScheduleDrawer(!showScheduleDrawer);
  };
  // #endregion

  // #region ** Schedule Drawer ** Event Handlers
  const closeScheduleCaseDrawer = () => {
    setShowScheduleDrawer(false);
  };

  const handleOnActivateDrawerTab = (currentTab) => {
    if (currentTab === 'case') {
      setDrawerActiveTab('case');
    } else if (currentTab === 'patient') {
      setDrawerActiveTab('patient');
    } else if (currentTab === 'payers') {
      setDrawerActiveTab('payers');
    }
  };

  const handleOnAddPatient = (data) => {
    // setPatientData(data);
    props.addPatientToCaseBlock(data);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeletePatient = () => {
    setPatientData({});
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnReloadPatientData = () => {
    props.reloadCaseBlockCasePatientData(props.caseBlockActiveCaseSchedule.patientId);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  // #endregion

  // #region ** Case Docs (new) ** Event Handlers

  // Face Sheet
  const handleOnFaceSheetDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setFaceSheetDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNoneFaceSheet = (value) => {
    handleOnCaseDataChange();
    setIsNoneFaceSheet(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneFaceSheet: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteFaceSheetPage = (docId) => {
    handleOnCaseDataChange();
    setFaceSheetDocs(prvState => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnRotate = (docId) => { };

  const handleOnDeleteFaceSheetDocument = () => {
    handleOnCaseDataChange();
    setFaceSheetDocs([]);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  // Consent Surgery
  const handleOnConsentSurgFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setConsentSurgFormDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNoneConsentSurgForm = (value) => {
    handleOnCaseDataChange();
    setIsNoneConsentSurgFormDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneConsentSurgFormDocs: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteConsentSurgFormDocPage = (docId) => {
    handleOnCaseDataChange();
    setConsentSurgFormDocs((prvState) => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteConsentSurgFormDocDocument = (docId) => {
    handleOnCaseDataChange();
    setConsentSurgFormDocs();
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  // Consent Anesthesia
  const handleOnConsentFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setConsentFormDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNoneConsentForm = (value) => {
    handleOnCaseDataChange();
    setIsNoneConsentFormDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneConsentFormDocs: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteConsentFormDocPage = (docId) => {
    handleOnCaseDataChange();
    setConsentFormDocs((prvState) => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteConsentFormDocDocument = (docId) => {
    handleOnCaseDataChange();
    setConsentFormDocs([]);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  // Anesthesia PrreOP Sub Tab
  const handleOnPreOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setPreOpFormDocs(docs);
  };

  const handleOnChangeIsNonePreOpForm = (value) => {
    handleOnCaseDataChange();
    setIsNonePreOpFormDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNonePreOpFormDocs: value });
  };

  const handleOnDeletePreOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    setPreOpFormDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeletePreOpFormmDocDocument = (docId) => {
    handleOnCaseDataChange();
    setPreOpFormDocs([]);
  };

  // Anesthesia IntraOp Sub Tab
  const handleOnIntraOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setIntraOpFormDocs(docs);
  };

  const handleOnChangeIsNoneIntraOpForm = (value) => {
    handleOnCaseDataChange();
    setIsNoneIntraOpFormDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneIntraOpFormDocs: value });
  };

  const handleOnDeleteIntraOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    setIntraOpFormDocs((prvState) => prvState.filter(el => el.id !== docId));
  };

  const handleOnDeleteIntraOpFormmDocDocument = (docId) => {
    handleOnCaseDataChange();
    setIntraOpFormDocs([]);
  };

  // Anesthesia PostOp Sub Tab
  const handleOnPostOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setPostOpFormDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNonePostOpForm = (value) => {
    handleOnCaseDataChange();
    setIsNonePostOpFormDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNonePostOpFormDocs: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeletePostOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    setPostOpFormDocs((prvState) => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeletePostOpFormmDocDocument = (docId) => {
    handleOnCaseDataChange();
    setPostOpFormDocs([]);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };
  // #endregion

  // #region Records Event Handlers
  const handleOnLabsDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setLabsDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNoneLabsDocs = (value) => {
    handleOnCaseDataChange();
    setIsNoneLabsDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneLabDocs: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  }

  const handleOnDeleteLabsDocsPage = (docId) => {
    handleOnCaseDataChange();
    setLabsDocs((prvState) => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteLabsDocsDocument = () => {
    handleOnCaseDataChange();
    setLabsDocs([]);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnEkgDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setEkgDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNoneEkgDocs = (value) => {
    handleOnCaseDataChange();
    setIsNoneEkgDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneEkgDocs: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  }

  const handleOnDeleteEkgDocsPage = (docId) => {
    handleOnCaseDataChange();
    setEkgDocs((prvState) => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteEkgDocsDocument = (docId) => {
    handleOnCaseDataChange();
    setEkgDocs([]);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnImagingDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setImagingDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNoneImagingDocs = (value) => {
    handleOnCaseDataChange();
    setIsNoneImagingDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneImagingDocs: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  }

  const handleOnDeleteImagingDocsPage = (docId) => {
    handleOnCaseDataChange();
    setImagingDocs((prvState) => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteImagingDocsDocument = (docId) => {
    handleOnCaseDataChange();
    setImagingDocs();
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnConsultsDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setConsultsDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNoneConsultsDocs = (value) => {
    handleOnCaseDataChange();
    setIsNoneConsults(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneConsults: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  }

  const handleOnDeleteConsultsDocsPage = (docId) => {
    handleOnCaseDataChange();
    setConsultsDocs((prvState) => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteConsultsDocsDocument = (docId) => {
    handleOnCaseDataChange();
    setConsultsDocs([]);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };
  // #endregion

  // #region Nursing Event Handlers
  const handleOnNursingDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setNursingDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNoneNursingDocs = (value) => {
    handleOnCaseDataChange();
    setIsNoneNursingDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneNursingDocs: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteNursingDocsPage = (docId) => {
    handleOnCaseDataChange();
    setNursingDocs((prvState) => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteNursingDocsDocument = (docId) => {
    handleOnCaseDataChange();
    setNursingDocs([]);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };
  // #endregion

  // #region Surgeon Event Handlers
  const handleOnSurgeonDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setSurgeonDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNoneSurgeonDocs = (value) => {
    handleOnCaseDataChange();
    setIsNoneSurgeonDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneSurgeonDocs: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteSurgeonDocsPage = (docId) => {
    handleOnCaseDataChange();
    setSurgeonDocs((prvState) => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteSurgeonDocsDocument = (docId) => {
    handleOnCaseDataChange();
    setSurgeonDocs([]);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };
  // #endregion

  // #region Discharage Event Handelers
  const handleOnDischargeDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setDischargeDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNoneDischarge = (value) => {
    handleOnCaseDataChange();
    setIsNoneDischargeDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneDischargeDocs: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteDischargeDocPage = (docId) => {
    handleOnCaseDataChange();
    setDischargeDocs(prvState => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteDischargeDocDocument = (docId) => {
    handleOnCaseDataChange();
    setDischargeDocs([]);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };
  // #endregion

  // #region Orders PreOp Tab Event Handlers
  const handleOnOrdersPreOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setOrdersPreOpDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNoneOrdersPreOpForm = (value) => {
    handleOnCaseDataChange();
    setIsNoneOrdersPreOpDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneOrdersPreOpDocs: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteOrdersPreOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    setOrdersPreOpDocs((prvState) => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteOrdersPreOpFormmDocDocument = (docId) => {
    handleOnCaseDataChange();
    setOrdersPreOpDocs([]);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };
  // #endregion

  // #region Orders IntraOp Event Handlers
  const handleOnOrdersIntraOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setOrdersIntraOpDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNoneOrdersIntraOpForm = (value) => {
    handleOnCaseDataChange();
    setIsNoneOrdersIntraOpDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneOrdersIntraOpDocs: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteOrdersIntraOpFormmDocPage = (docId) => {
    handleOnCaseDataChange();
    setOrdersIntraOpDocs((prvState) => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteOrdersIntraOpFormmDocDocument = (docId) => {
    handleOnCaseDataChange();
    setOrdersIntraOpDocs([]);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };
  // #endregion

  // #region Order PreOp Event Handlers
  const handleOnOrdersPostOpFormDocsUploaded = (docs) => {
    handleOnCaseDataChange();
    setOrdresPostOpDocs(docs);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnChangeIsNoneOrdersPostOpForm = (value) => {
    handleOnCaseDataChange();
    setIsNoneOrdersPostOpDocs(value);
    props.updateCaseBlockActiveCaseSchedule({ isNoneOrdersPostOpDocs: value });
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteOrdersPostOpFormDocPage = (docId) => {
    handleOnCaseDataChange();
    setOrdresPostOpDocs((prvState) => prvState.filter(el => el.id !== docId));
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };

  const handleOnDeleteOrdersPostOpFormDocDocument = (docId) => {
    handleOnCaseDataChange();
    setOrdresPostOpDocs([]);
    if (!props.shoudlSaveCaseData) {
      props.setShouldSaveCaseData(true);
    }
  };
  // #endregion

  /* _____ MENU ITEMS ________________________________________________ */
  // #region * MENU ITEMS * -- Anesthesia Procedures
  const procedureSearchItems = [];
  // Append search text
  if (anesthesiaProcedureValue && anesthesiaProcedureValue.length > 0) {
    procedureSearchItems.push(
      <div
        key='88888999SurgProcedure'
        className={`fx fx__v-center ${css(styles.menuItem)}`}
        onClick={(e) => {
          e.stopPropagation();
          const tmpProc = {
            id: Math.random().toString(36).slice(2),
            text: anesthesiaProcedureValue,
            code: '',
            description: anesthesiaProcedureValue,
            npi: 0
          };
          handleOnCaseDataChange();
          setShowAnesthesiaProceduresSearchMenu(false);
          setAnesthesiaProcedureValue('');
          setAnesthesiaProcedures((prevState) => {
            return prevState.concat(tmpProc);
          });
        }}
      >
        <div className='ml-2'>
          <div style={{ height: '40px', display: 'flex', alignItems: 'center' }}>
            {anesthesiaProcedureValue}
          </div>
        </div>
      </div>
    );
  }
  if (anesthesiaProceduresSearchResults.length > 0) {
    // Append search results
    anesthesiaProceduresSearchResults.forEach((el) => {
      procedureSearchItems.push(
        <div
          key={el._id}
          className={`fx fx__v-center ${css(styles.menuItem)}`}
          onClick={(e) => {
            e.stopPropagation();
            handleOnCaseDataChange();
            setShowAnesthesiaProceduresSearchMenu(false);
            setAnesthesiaProcedureValue('');
            setAnesthesiaProcedures((prevState) => {
              if (prevState.findIndex(item => item.id === el._id) < 0) {
                return prevState.concat({
                  id: el._id,
                  text: `${el.description}`,
                  code: el.code,
                  description: `${el.description}`,
                  npi: el.npi
                });
              } else {
                return prevState;
              }
            });
          }}
        >
          <div className='ml-2'>
            <div style={{ height: '40px', display: 'flex', alignItems: 'center' }}>
              {getHighlightedText(el.description, anesthesiaProcedureValue)}
            </div>
          </div>
        </div>
      );
    });
  }
  // #endregion

  let activeDrawerComponent = null;
  switch (drawerActiveTab) {
    case 'case':
      activeDrawerComponent = (
        <CaseTab
          isDashboard
          forCaseBlocks={props.forCaseBlocks}
          onClose={closeScheduleCaseDrawer}
          showFooter={false}
          onDataChange={handleOnCaseDataChange}
        />
      );
      break;
    case 'patient':
      activeDrawerComponent = (
        <PatientTab
          patientData={patientData}
          onAddPatient={handleOnAddPatient}
          onDeletePatient={handleOnDeletePatient}
          onReloadPatientData={handleOnReloadPatientData}
          onChangeFlag={handleOnChangePatientFlag}
        />
      );
      break;
    case 'payers':
      activeDrawerComponent = (
        <PayerTab
          patientData={props.patientData}
          onReloadPatientData={handleOnReloadPatientData}
        />
      );
      break;
    default:
      activeDrawerComponent = null;
  }

  let activeDocComponent = null;
  switch (activeTab) {
    case TABS[0].text: // FaceSheet
      activeDocComponent = (
        <CaseDoc
          caseDocs={faceSheetDocs}
          isNone={isNoneFaceSheet}
          showFlag
          flagType={FLAG_VALUE.critical}
          onChangeIsNone={handleOnChangeIsNoneFaceSheet}
          onCaseDocUploaded={handleOnFaceSheetDocsUploaded}
          onDeletePage={handleOnDeleteFaceSheetPage}
          onDeleteDocument={handleOnDeleteFaceSheetDocument}
          onRotate={handleOnRotate}
        />
      );
      break;
    case TABS[1].text: // Records
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="records_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* Labs */}
            <TabItem
              key='1199'
              tabKey='1199'
              parentKey='11999'
              text='Labs'
              isActive={recordsSubTab === 'labs'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneLabsDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (labsDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: window.innerWidth > 720 ? '110px' : '25%',
                minWidth: window.innerWidth > 720 ? '110px' : '25%',
                maxWidth: window.innerWidth > 720 ? '110px' : '25%',
                background: recordsSubTab === 'labs' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setRecordsSubTab('labs');
              }}
            >
              Labs
            </TabItem>
            {/* EKG */}
            <TabItem
              key='12992001'
              tabKey='12992001'
              parentKey='12993000'
              text='EKG'
              isActive={recordsSubTab === 'ekg'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneEkgDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (ekgDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: window.innerWidth > 720 ? '110px' : '25%',
                minWidth: window.innerWidth > 720 ? '110px' : '25%',
                maxWidth: window.innerWidth > 720 ? '110px' : '25%',
                background: recordsSubTab === 'ekg' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setRecordsSubTab('ekg');
              }}
            >
              EKG
            </TabItem>
            {/* Imaging */}
            <TabItem
              key='19192002'
              tabKey='19192002'
              parentKey='19194000'
              text='PostOp'
              isActive={recordsSubTab === 'imaging'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneImagingDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (imagingDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: window.innerWidth > 720 ? '110px' : '25%',
                minWidth: window.innerWidth > 720 ? '110px' : '25%',
                maxWidth: window.innerWidth > 720 ? '110px' : '25%',
                background: recordsSubTab === 'imaging' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setRecordsSubTab('imaging');
              }}
            >
              Imaging
            </TabItem>
            {/* Consults */}
            <TabItem
              key='2003'
              tabKey='2003'
              parentKey='5000'
              text='Procedures'
              isActive={recordsSubTab === 'consults'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneConsults) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (consultsDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: window.innerWidth > 720 ? '110px' : '25%',
                minWidth: window.innerWidth > 720 ? '110px' : '25%',
                maxWidth: window.innerWidth > 720 ? '110px' : '25%',
                background: recordsSubTab === 'consults' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setRecordsSubTab('consults');
              }}
            >
              Consults
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {recordsSubTab === 'labs' &&
              <CaseDoc
                caseDocs={labsDocs}
                isNone={isNoneLabsDocs}
                showFlag
                flagType={FLAG_VALUE.critical}
                onChangeIsNone={handleOnChangeIsNoneLabsDocs}
                onCaseDocUploaded={handleOnLabsDocsUploaded}
                onDeletePage={handleOnDeleteLabsDocsPage}
                onDeleteDocument={handleOnDeleteLabsDocsDocument}
              />
            }
            {recordsSubTab === 'ekg' &&
              <CaseDoc
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={ekgDocs}
                isNone={isNoneEkgDocs}
                onChangeIsNone={handleOnChangeIsNoneEkgDocs}
                onCaseDocUploaded={handleOnEkgDocsUploaded}
                onDeletePage={handleOnDeleteEkgDocsPage}
                onDeleteDocument={handleOnDeleteEkgDocsDocument}
              />
            }
            {recordsSubTab === 'imaging' &&
              <CaseDoc
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={imagingDocs}
                isNone={isNoneImagingDocs}
                onChangeIsNone={handleOnChangeIsNoneImagingDocs}
                onCaseDocUploaded={handleOnImagingDocsUploaded}
                onDeletePage={handleOnDeleteImagingDocsPage}
                onDeleteDocument={handleOnDeleteImagingDocsDocument}
              />
            }
            {recordsSubTab === 'consults' &&
              <CaseDoc
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={consultsDocs}
                isNone={isNoneConsults}
                onChangeIsNone={handleOnChangeIsNoneConsultsDocs}
                onCaseDocUploaded={handleOnConsultsDocsUploaded}
                onDeletePage={handleOnDeleteConsultsDocsPage}
                onDeleteDocument={handleOnDeleteConsultsDocsDocument}
              />
            }
          </div>
        </>
      );
      break;
    case TABS[2].text: // Consent Forms
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="consent_forms_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* Consents - Surgery sub tab */}
            <TabItem
              key='2334'
              tabKey='2334'
              parentKey='1223'
              text='Surgery'
              isActive={consentActiveTab === 'surgery'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneConsentSurgFormDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (consentSurgFormDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: consentActiveTab === 'surgery' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setConsentActiveTab('surgery');
              }}
            >
              Surgery
            </TabItem>
            {/* Consents - Anesthesia sub tab */}
            <TabItem
              key='2333'
              tabKey='2333'
              parentKey='1222'
              text='Anesthesia'
              isActive={consentActiveTab === 'anesthesia'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneConsentFormDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (consentFormDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: consentActiveTab === 'anesthesia' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setConsentActiveTab('anesthesia');
              }}
            >
              Anesthesia
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {consentActiveTab === 'surgery' &&
              <CaseDoc
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={consentSurgFormDocs}
                isNone={isNoneConsentSurgFormDocs}
                onChangeIsNone={handleOnChangeIsNoneConsentSurgForm}
                onCaseDocUploaded={handleOnConsentSurgFormDocsUploaded}
                onDeletePage={handleOnDeleteConsentSurgFormDocPage}
                onDeleteDocument={handleOnDeleteConsentSurgFormDocDocument}
              />
            }
            {consentActiveTab === 'anesthesia' &&
              <CaseDoc
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={consentFormDocs}
                isNone={isNoneConsentFormDocs}
                onChangeIsNone={handleOnChangeIsNoneConsentForm}
                onCaseDocUploaded={handleOnConsentFormDocsUploaded}
                onDeletePage={handleOnDeleteConsentFormDocPage}
                onDeleteDocument={handleOnDeleteConsentFormDocDocument}
              />
            }
          </div>
        </>
      );
      break;
    case TABS[3].text: // Nursing
      activeDocComponent = (
        <CaseDoc
          caseDocs={nursingDocs}
          isNone={isNoneNursingDocs}
          showFlag
          flagType={FLAG_VALUE.critical}
          onChangeIsNone={handleOnChangeIsNoneNursingDocs}
          onCaseDocUploaded={handleOnNursingDocsUploaded}
          onDeletePage={handleOnDeleteNursingDocsPage}
          onDeleteDocument={handleOnDeleteNursingDocsDocument}
        />
      );
      break;
    case TABS[4].text: // Surgeon
      activeDocComponent = (
        <CaseDoc
          caseDocs={surgeonDocs}
          isNone={isNoneSurgeonDocs}
          showFlag
          flagType={FLAG_VALUE.critical}
          onChangeIsNone={handleOnChangeIsNoneSurgeonDocs}
          onCaseDocUploaded={handleOnSurgeonDocsUploaded}
          onDeletePage={handleOnDeleteSurgeonDocsPage}
          onDeleteDocument={handleOnDeleteSurgeonDocsDocument}
        />
      );
      break;
    case TABS[5].text: // Anesthesia Forms
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="anesthesia_forms_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* PreOp Tab */}
            <TabItem
              key='2000'
              tabKey='2000'
              parentKey='2000'
              text='PreOp'
              isActive={anesthesiaSubTab === 'preOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                const anesthesiaDataFlagValue = asaScore.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
                if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
                  return <DocumentCriticalIcon height={20} />;
                } else {
                  if (isNonePreOpFormDocs) {
                    return <DocumentSuccessIcon height={20} />;
                  }
                  if (preOpFormDocs.length > 0) {
                    return <DocumentSuccessIcon height={20} />;
                  } else {
                    return <DocumentCriticalIcon height={20} />;
                  }
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: window.innerWidth > 720 ? '110px' : '22%',
                minWidth: window.innerWidth > 720 ? '110px' : '22%',
                maxWidth: window.innerWidth > 720 ? '110px' : '22%',
                background: anesthesiaSubTab === 'preOp' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setAnesthesiaSubTab('preOp');
              }}
            >
              PreOp
            </TabItem>
            {/* IntraOp Tab */}
            <TabItem
              key='2001'
              tabKey='2001'
              parentKey='3000'
              text='IntraOp'
              isActive={anesthesiaSubTab === 'intraOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                let anesthesiaDataFlagValue = FLAG_VALUE.critical;
                if (anesthesiaTypes.length > 0 && anesthesiaModifiers.length > 0) {
                  anesthesiaDataFlagValue = FLAG_VALUE.success;
                }
                if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
                  return <DocumentCriticalIcon height={20} />;
                } else {
                  if (isNoneIntraOpFormDocs) {
                    return <DocumentSuccessIcon height={20} />;
                  }
                  if (intraOpFormDocs.length > 0) {
                    return <DocumentSuccessIcon height={20} />;
                  } else {
                    return <DocumentCriticalIcon height={20} />;
                  }
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: window.innerWidth > 720 ? '110px' : '25%',
                minWidth: window.innerWidth > 720 ? '110px' : '25%',
                maxWidth: window.innerWidth > 720 ? '110px' : '25%',
                background: anesthesiaSubTab === 'intraOp' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setAnesthesiaSubTab('intraOp');
              }}
            >
              IntraOp
            </TabItem>
            {/* PostOp */}
            <TabItem
              key='2002'
              tabKey='2002'
              parentKey='4000'
              text='PostOp'
              isActive={anesthesiaSubTab === 'postOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNonePostOpFormDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (postOpFormDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: window.innerWidth > 720 ? '110px' : '25%',
                minWidth: window.innerWidth > 720 ? '110px' : '25%',
                maxWidth: window.innerWidth > 720 ? '110px' : '25%',
                background: anesthesiaSubTab === 'postOp' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setAnesthesiaSubTab('postOp');
              }}
            >
              PostOp
            </TabItem>
            {/* Procedures */}
            <TabItem
              key='2003'
              tabKey='2003'
              parentKey='5000'
              text='Procedures'
              isActive={anesthesiaSubTab === 'procedures'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneAnesthesiaProcedure) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (anesthesiaProcedures.length === 0) {
                  return <DocumentCriticalIcon height={20} />;
                }
                if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
                  return <DocumentWarningIcon height={20} />;
                } else {
                  return <DocumentSuccessIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: window.innerWidth > 720 ? '110px' : '28%',
                minWidth: window.innerWidth > 720 ? '110px' : '28%',
                maxWidth: window.innerWidth > 720 ? '110px' : '28%',
                background: anesthesiaSubTab === 'procedures' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setAnesthesiaSubTab('procedures');
              }}
            >
              Procedures
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {anesthesiaSubTab === 'preOp' &&
              <PreOp
                showFlag
                asaScore={asaScore}
                onChangeAsaScore={(asa) => {
                  setAsaScore(asa);
                  props.updateCaseBlockActiveCaseSchedule({ asaScore: asa });
                }}
                flagType={FLAG_VALUE.critical}
                caseDocs={preOpFormDocs}
                isNone={isNonePreOpFormDocs}
                onChangeIsNone={handleOnChangeIsNonePreOpForm}
                onCaseDocUploaded={handleOnPreOpFormDocsUploaded}
                onDeletePage={handleOnDeletePreOpFormmDocPage}
                onDeleteDocument={handleOnDeletePreOpFormmDocDocument}
              />
            }
            {anesthesiaSubTab === 'intraOp' &&
              <IntraOp
                showFlag
                asaScore={asaScore}
                onChangeAsaScore={(asa) => {
                  setAsaScore(asa);
                  props.updateCaseBlockActiveCaseSchedule({ asaScore: asa });
                }}
                anesthesiaTypes={anesthesiaTypes}
                onChangeAnesthesiaTypes={(data) => {
                  setAnesthesiaTypes(data);
                  props.updateCaseBlockActiveCaseSchedule({ anesthesiaTypes: data });
                }}
                anesthesiaModifiers={anesthesiaModifiers}
                onChangeAnesthesiaModifiers={(data) => {
                  setAnesthesiaModifiers(data);
                  props.updateCaseBlockActiveCaseSchedule({ anesthesiaModifiers: data });
                }}
                flagType={FLAG_VALUE.critical}
                caseDocs={intraOpFormDocs}
                isNone={isNoneIntraOpFormDocs}
                onChangeIsNone={handleOnChangeIsNoneIntraOpForm}
                onCaseDocUploaded={handleOnIntraOpFormDocsUploaded}
                onDeletePage={handleOnDeleteIntraOpFormmDocPage}
                onDeleteDocument={handleOnDeleteIntraOpFormmDocDocument}
              />
            }
            {anesthesiaSubTab === 'postOp' &&
              <CaseDoc
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={postOpFormDocs}
                isNone={isNonePostOpFormDocs}
                onChangeIsNone={handleOnChangeIsNonePostOpForm}
                onCaseDocUploaded={handleOnPostOpFormDocsUploaded}
                onDeletePage={handleOnDeletePostOpFormmDocPage}
                onDeleteDocument={handleOnDeletePostOpFormmDocDocument}
              />
            }
            {anesthesiaSubTab === 'procedures' &&
              <div
                className='m-2'
                style={{ width: `${DRAWER_WIDTH}px` }}
              >
                <div
                  className='mt-4 mb-2 fx fx__v-center'
                  style={{
                    width: '355px',
                    justifyContent: 'space-between'
                  }}
                >
                  <CaseField
                    heading='Anesthesia Procedures'
                    // showAlert={false}
                    showNoneFlag={isNoneAnesthesiaProcedure}
                    showAlert={(() => {
                      if (!isNoneAnesthesiaProcedure) {
                        if (anesthesiaProcedures.length === 0) {
                          return true;
                        }
                        return false;
                      }
                    })()}
                    showIncomplete={(() => {
                      if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
                        return true;
                      } else {
                        return false;
                      }
                    })()}
                    showPlusIcon={false}
                    className='ml-1'
                  />
                  <Checkbox
                    text='None'
                    isChecked={isNoneAnesthesiaProcedure}
                    onChange={() => {
                      handleOnCaseDataChange();
                      setAnesthesiaProcedures([]);
                      setIsNoneAnesthesiaProcedure(!isNoneAnesthesiaProcedure);
                      props.updateCaseBlockActiveCaseSchedule({ isNoneAnesthesiaProcedure: !isNoneAnesthesiaProcedure });
                    }}
                  />
                </div>
                {/* Anesthesia procedure value textbox & search menu */}
                <div
                  ref={procedureSearchMenuRef}
                  className='mt-0'
                  style={{
                    // border: `1px solid ${blue[700]}`,
                    borderRadius: '4px',
                    position: 'relative'
                  }}
                >
                  <TextInput
                    value={anesthesiaProcedureValue}
                    isDisabled={isNoneAnesthesiaProcedure}
                    placeholder="Search Anesthesia Procedure(s)"
                    disabled={isNoneAnesthesiaProcedure}
                    style={{
                      width: `${DRAWER_WIDTH}px`,
                      fontSize: '14px',
                      padding: '0 4px',
                      color: blue[200],
                      borderRadius: '4px'
                    }}
                    onChange={(e) => {
                      const { value } = e.target;
                      setAnesthesiaProcedureValue(value);
                    }}
                  />
                  {showAnesthesiaProceduresSearchMenu &&
                    <PopupMenu
                      top={35}
                      left={0}
                      zIndex='1550'
                      position="absolute"
                      visible={showAnesthesiaProceduresSearchMenu}
                      parent={procedureSearchMenuRef}
                      onOutsideClick={() => { setShowAnesthesiaProceduresSearchMenu(false); }}
                    >
                      <div className={css(styles.searchPopupMenuContainer)}>
                        {procedureSearchItems.length > 0 &&
                          <div className={`pl-2 fx fx__v-center ${css(styles.suggResults)}`}>
                            Suggested Results
                          </div>
                        }
                        {procedureSearchItems}
                      </div>
                    </PopupMenu>
                  }
                </div>
                {/* Surgical Procedures Cards */}
                {anesthesiaProcedures.map(dx => {
                  return (
                    <CaseUserCard
                      className='mt-1'
                      avatarImageUrl={null}
                      showCloseIcon
                      noAvatar
                      key={dx.id || dx._id}
                      dataId={dx.id || dx._id}
                      onClose={(id) => {
                        handleOnCaseDataChange();
                        setAnesthesiaProcedures((prevState) => {
                          return prevState.filter((el) => {
                            if (el.id) {
                              return el.id !== id;
                            }
                            return el._id !== id;
                          });
                        });
                      }}
                    >
                      <div className='ml-2'>
                        {dx.npi === 0 &&
                          <i
                            className='font-16 mr-2 fa fa-bell'
                            style={{ color: salmon[500] }}
                          />
                        }
                        <span>
                          {dx.description}
                        </span>
                      </div>
                    </CaseUserCard>
                  );
                })}
              </div>
            }
          </div>
        </>
      );
      break;
    case TABS[6].text: // Orders
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="orders_forms_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* Orders - PreOp Tab */}
            <TabItem
              key='2000334'
              tabKey='2000334'
              parentKey='2000334'
              text='PreOp'
              isActive={ordersSubTab === 'preOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneOrdersPreOpDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (ordersPreOpDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: ordersSubTab === 'preOp' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setOrdersSubTab('preOp');
              }}
            >
              PreOp
            </TabItem>
            {/* IntraOp Tab */}
            <TabItem
              key='2001334'
              tabKey='2001334'
              parentKey='3000334'
              text='IntraOp'
              isActive={ordersSubTab === 'intraOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (isNoneOrdersIntraOpDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (ordersIntraOpDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: ordersSubTab === 'intraOp' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setOrdersSubTab('intraOp');
              }}
            >
              IntraOp
            </TabItem>
            {/* PostOp */}
            <TabItem
              key='2002'
              tabKey='2002'
              parentKey='4000'
              text='PostOp'
              isActive={ordersSubTab === 'postOp'}
              showTabIcon
              showSvgIcon
              // svgIcon={(postOpFormDocs.length > 0 || isNonePostOpFormDocs) ? SUCCESS_ICON : BELL_ICON}
              svgIcon={(() => {
                if (isNoneOrdersPostOpDocs) {
                  return IS_NONE_ICON;
                }
                if (ordresPostOpDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: ordersSubTab === 'postOp' ? '#184878' : '#083050'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setOrdersSubTab('postOp');
              }}
            >
              PostOp
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {ordersSubTab === 'preOp' &&
              <CaseDoc
                caseDocs={ordersPreOpDocs}
                isNone={isNoneOrdersPreOpDocs}
                showFlag
                flagType={FLAG_VALUE.critical}
                onChangeIsNone={handleOnChangeIsNoneOrdersPreOpForm}
                onCaseDocUploaded={handleOnOrdersPreOpFormDocsUploaded}
                onDeletePage={handleOnDeleteOrdersPreOpFormmDocPage}
                onDeleteDocument={handleOnDeleteOrdersPreOpFormmDocDocument}
              />
            }
            {ordersSubTab === 'intraOp' &&
              <CaseDoc
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={ordersIntraOpDocs}
                isNone={isNoneOrdersIntraOpDocs}
                onChangeIsNone={handleOnChangeIsNoneOrdersIntraOpForm}
                onCaseDocUploaded={handleOnOrdersIntraOpFormDocsUploaded}
                onDeletePage={handleOnDeleteOrdersIntraOpFormmDocPage}
                onDeleteDocument={handleOnDeleteOrdersIntraOpFormmDocDocument}
              />
            }
            {ordersSubTab === 'postOp' &&
              <CaseDoc
                showFlag
                flagType={FLAG_VALUE.critical}
                caseDocs={ordresPostOpDocs}
                isNone={isNoneOrdersPostOpDocs}
                onChangeIsNone={handleOnChangeIsNoneOrdersPostOpForm}
                onCaseDocUploaded={handleOnOrdersPostOpFormDocsUploaded}
                onDeletePage={handleOnDeleteOrdersPostOpFormDocPage}
                onDeleteDocument={handleOnDeleteOrdersPostOpFormDocDocument}
              />
            }
          </div>
        </>
      );
      break;
    case TABS[7].text: // Discharge
      activeDocComponent = (
        <CaseDoc
          caseDocs={dischargeDocs}
          isNone={isNoneDischargeDocs}
          showFlag
          flagType={FLAG_VALUE.critical}
          onChangeIsNone={handleOnChangeIsNoneDischarge}
          onCaseDocUploaded={handleOnDischargeDocsUploaded}
          onDeletePage={handleOnDeleteDischargeDocPage}
          onDeleteDocument={handleOnDeleteDischargeDocDocument}
        />
      );
      break;
    default:
      activeDocComponent = null;
  }

  debugLogToConsole(props);
  return (
    <div className={css(styles.dashboardContainer)}>
      {/* Saved Msg -- Toast */}
      {showSavedMsg &&
        <Toast
          id={'c1'}
          top="41"
          bottom={null}
          headerText='Information'
          headerIcon='fa fa-info-circle'
          bodyText='Schedule Data Saved!'
          onDismiss={() => {
            setShowSavedMsg(false);
          }}
          showFooter={false}
        />
      }
      {/* Discad Dialog 1 */}
      {showDisacardDialog1 &&
        <DialogBox
          showCloseBtn={false}
          initWidth={DIALOG_WIDTH}
          top={window.innerWidth <= 576 ? 48 : null}
          left={window.innerWidth <= 576 ? 8 : null}
          initHeight={265}
          isOpen={showDisacardDialog1}
          onClose={() => {
            setShowDiscardDialog1(false);
          }}
          maskBackground="#004080"
          maskOpacity={0.5}
          closeOnEscKey
          closeOnMaskClick={false}
          header={{
            overrideTheme: true,
            text: 'Warning! You Have Unsaved Changes!',
            backColor: '#082840',
            foreColor: 'yellow',
            closeColor: '#FF6060',
            fontStyle: 'bold',
            fontSize: '14px',
            icon: 'fa fa-exclamation-triangle'
          }}
        >
          <div className='p-2'>
            <div className='p-2 text-center'>
              What do you want to do?
            </div>
            {/* Save Button */}
            <Button
              className='mt-2'
              btnType='primary'
              style={{
                height: '47px',
                width: '334px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}
              onClick={() => {
                debugLogToConsole('Not implemented yet');
              }}
            >
              Save
            </Button>
            {/* Do tnot save Button */}
            <Button
              className='mt-4'
              btnType='danger'
              style={{
                height: '32px',
                width: '334px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => {
                setShowDiscardDialog1(false);
                setShowDiscardDialog2(true);
              }}
            >
              Do Not Save
            </Button>
            {/* Cancel Button */}
            <Button
              className='mt-4'
              btnType='primary'
              isOutlineBtn
              style={{
                border: `1px solid ${gray[400]}`,
                color: gray[300],
                height: '32px',
                width: '334px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => setShowDiscardDialog1(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogBox>
      }

      {/* Discad Dialog 2 */}
      {showDisacardDialog2 &&
        <DialogBox
          showCloseBtn={false}
          initWidth={DIALOG_WIDTH}
          top={window.innerWidth <= 576 ? 48 : null}
          left={window.innerWidth <= 576 ? 8 : null}
          initHeight={265}
          isOpen={showDisacardDialog2}
          onClose={() => {
            setShowDiscardDialog2(false);
          }}
          maskBackground="#004080"
          maskOpacity={0.5}
          closeOnEscKey
          closeOnMaskClick={false}
          header={{
            overrideTheme: true,
            text: 'Really?',
            backColor: '#082840',
            foreColor: 'yellow',
            closeColor: '#FF6060',
            fontStyle: 'bold',
            fontSize: '14px',
            icon: 'fa fa-exclamation-triangle'
          }}
        >
          <div className='p-2'>
            <div className='p-2 text-center'>
              Are you 100% sure?
            </div>
            {/* Save Button */}
            <Button
              className='mt-2'
              btnType='primary'
              style={{
                height: '47px',
                width: '334px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}
              onClick={() => {
                debugLogToConsole('Not implemented yet');
              }}
            >
              Save
            </Button>
            {/* Do not save Button */}
            <Button
              className='mt-4'
              btnType='danger'
              style={{
                height: '32px',
                width: '334px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => {
                setShowDiscardDialog2(false);
                props.onClose();
              }}
            >
              Do Not Save... I am 100% sure
            </Button>
            {/* Cancel Button */}
            <Button
              className='mt-4'
              btnType='primary'
              isOutlineBtn
              style={{
                border: `1px solid ${gray[400]}`,
                color: gray[300],
                height: '32px',
                width: '334px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => setShowDiscardDialog2(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogBox>
      }

      {/* --  Main Header ------------------------------------------- */}
      <div className={css(styles.header)}>
        {/* Left side */}
        <div className='fx fx__hv-center'>
          <div className={`pl-1 ${css(styles.headerText)}`}>
            {caseData.facility && caseData.facility.icon &&
              <img
                className='mr-2'
                src={caseData.facility.icon}
                style={{
                  maxHeight: '32px'
                }}
              />
            }
            {caseData.facility && caseData.facility.facilityName
              ? caseData.facility.facilityName
              : 'Faciity Name N/A'
            }
          </div>
        </div>
        {/* Right side - (Collapse Icon) */}
        <div className='fx fx__hv-center'>
          {/* Schedule Master Flag */}
          {scheduleMasterFlag === FLAG_VALUE.success && SUCCESS_ICON}
          {scheduleMasterFlag === FLAG_VALUE.warning && BELL_ICON}
          {scheduleMasterFlag === FLAG_VALUE.critical && CRITIAL_ICON}
          {/* Header Popup Menu */}
          <div
            className={`ml-3 ${css(styles.ellipsisBtn)}`}
            ref={headerMenuRef}
          >
            <i
              className='fa fa-ellipsis-h'
              onClick={() => {
                setShowHeaderMenu(true);
              }}
            />
            {showHeaderMenu &&
              <PopupMenu
                top={32}
                left={0}
                zIndex='1550'
                position="absolute"
                visible={showHeaderMenu}
                parent={headerMenuRef}
                onOutsideClick={() => { setShowHeaderMenu(false); }}
              >
                <div
                  className={css(styles.searchPopupMenuContainer)}
                  style={{
                    width: '140px'
                  }}
                >
                  {/* Save and Close */}
                  <div
                    className={`pl-2 fx fx__v-center ${css(styles.menuItem)}`}
                    style={{ height: '32px' }}
                    onClick={() => {
                      setShowHeaderMenu(false);
                      props.markCaseForUpdate()
                      props.onClose();
                    }}
                  >
                    Save &amp; Close
                  </div>
                </div>
              </PopupMenu>
            }
          </div>
          {/* Collapse Icon - (was Disabled 07/04/23) */}
          <div
            className='ml-2 mr-2' style={{ height: '24px' }}
            onClick={() => {
              props.markCaseForUpdate()
              props.onClose();
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
            >
              <path fill="#96C8FF" fillRule="evenodd" d="M1.926 0h20.145C23.136 0 23.997.861 24 1.926v20.148A1.925 1.925 0 0 1 22.074 24H1.926A1.925 1.925 0 0 1 0 22.074V1.926C0 .861.861 0 1.926 0Zm11.549 11.14h5.738a.786.786 0 1 0 0-1.57H15.44l4.149-4.144a.784.784 0 0 0-.556-1.34.783.783 0 0 0-.556.23l-4.214 4.209v-3.74a.786.786 0 0 0-1.573 0v5.572a.783.783 0 0 0 .785.784ZM4.788 14.43h3.775l-4.15 4.144a.786.786 0 0 0 1.112 1.11l4.216-4.208v3.74a.786.786 0 0 0 1.574 0v-5.572a.786.786 0 0 0-.787-.786H4.789a.786.786 0 0 0-.001 1.572Z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
      {/* --  Main Dashboard Container ------------------------------ */}
      <div className={css(styles.sectionsContainer)}>
        {/* --- Case Docs Tabs -------- */}
        <div className={css(styles.tabsContainer)}>
          {/* Patient Sticker Container */}
          <div className={css(styles.patientStickerContainer)}>
            <PatientCard
              onClickCase={openCasePatientDrawer}
              flagType={masterFlag}
              patientData={{
                dos: props.caseBlockActiveCaseSchedule && props.caseBlockActiveCaseSchedule.scheduleStartDate
                  ? props.caseBlockActiveCaseSchedule.scheduleStartDate
                  : new Date().toISOString(),
                patientType: props.caseBlockActiveCaseSchedule && props.caseBlockActiveCaseSchedule.patientType
                  ? props.caseBlockActiveCaseSchedule.patientType
                  : '',
                facilityName: props.caseBlockActiveCaseSchedule && props.caseBlockActiveCaseSchedule.facility
                  ? props.caseBlockActiveCaseSchedule.facility.facilityName || ''
                  : '',
                surgeons: props.caseBlockActiveCaseSchedule && props.caseBlockActiveCaseSchedule.surgeons
                  ? props.caseBlockActiveCaseSchedule.surgeons
                  : [],
                admDx: props.caseBlockActiveCaseSchedule && props.caseBlockActiveCaseSchedule.preOpDx
                  ? props.caseBlockActiveCaseSchedule.preOpDx
                  : [],
                surgicalProcedures: props.caseBlockActiveCaseSchedule && props.caseBlockActiveCaseSchedule.surgicalProcedures
                  ? props.caseBlockActiveCaseSchedule.surgicalProcedures
                  : [],
                otherSurgicalProcedures: props.caseBlockActiveCaseSchedule && props.caseBlockActiveCaseSchedule.otherSurgicalProcedures
                  ? props.caseBlockActiveCaseSchedule.otherSurgicalProcedures
                  : [],
                anesthesia: props.caseBlockActiveCaseSchedule && props.caseBlockActiveCaseSchedule.anesthesiologists
                  ? props.caseBlockActiveCaseSchedule.anesthesiologists
                  : [],
                dob: patientData && patientData.dob ? patientData.dob : '',
                gender: patientData && patientData.gender ? patientData.gender : '',
                firstName: patientData && patientData.firstName ? patientData.firstName : '',
                middleName: patientData && patientData.middleName ? patientData.middleName : '',
                lastName: patientData && patientData.lastName ? patientData.lastName : ''
              }}
            />
          </div>

          {/* Document Tabs */}
          <div className={css(styles.docTabs)}>
            <TabsContainer
              dropDownId="caseSchedule_main_tabs"
              showPlusIcon={false}
              isDrawer={false}
              shiftMethod='minWidth'
              minWidth={610}
              containerStyles={{ width: '100%' }}
            >
              {TABS.map((tab) => {
                if (window.innerWidth <= 720) {
                  if (!tab.hideOnMobile) {
                    return (
                      <TabItem
                        key={tab.id}
                        tabKey={tab.id}
                        parentKey={tab.pKey}
                        text={tab.text}
                        isActive={activeTab === tab.text}
                        showTabIcon
                        showSvgIcon
                        svgIcon={(() => {
                          switch (tab.text) {
                            case 'FaceSheet':
                              if (isNoneFaceSheet) {
                                return IS_NONE_ICON;
                              }
                              if (faceSheetDocs.length > 0 || isNoneFaceSheet) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            case 'Records': {
                              if (isNoneLabsDocs && isNoneEkgDocs && isNoneImagingDocs && isNoneConsults) {
                                return IS_NONE_ICON;
                              }

                              const labsFlag = labsDocs.length > 0 || isNoneLabsDocs;
                              const ekgFlag = ekgDocs.length > 0 || isNoneEkgDocs;
                              const imagingFlag = imagingDocs.length > 0 || isNoneImagingDocs;
                              const consultsFlag = consultsDocs.length > 0 || isNoneConsults;

                              if (labsFlag && ekgFlag && imagingFlag && consultsFlag) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            }
                            case 'Consents': {
                              if (isNoneConsentSurgFormDocs && isNoneConsentFormDocs) {
                                return IS_NONE_ICON;
                              }

                              const surgFlag = consentSurgFormDocs.length > 0 || isNoneConsentSurgFormDocs;
                              const anesFlag = consentFormDocs.length > 0 || isNoneConsentFormDocs;

                              if (surgFlag && anesFlag) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            }
                            case 'Anesthesia': {
                              // PreOp Tab Flag
                              let preOpFlagValue = FLAG_VALUE.critical;
                              let anesthesiaAesFlagValue = FLAG_VALUE.critical;
                              if (asaScore.length > 0) {
                                anesthesiaAesFlagValue = FLAG_VALUE.success;
                              }
                              if (anesthesiaAesFlagValue === FLAG_VALUE.critical) {
                                preOpFlagValue = anesthesiaAesFlagValue;
                              } else {
                                if (isNonePreOpFormDocs) {
                                  preOpFlagValue = FLAG_VALUE.none;
                                } else {
                                  if (preOpFormDocs.length > 0) {
                                    preOpFlagValue = FLAG_VALUE.success;
                                  } else {
                                    preOpFlagValue = FLAG_VALUE.critical;
                                  }
                                }
                              }

                              // IntraOp Tab Flag
                              let intraOpFlagValue = FLAG_VALUE.critical;
                              let anesthesiaDataFlagValue = FLAG_VALUE.critical;
                              if (anesthesiaTypes.length > 0 && anesthesiaModifiers.length > 0) {
                                anesthesiaDataFlagValue = FLAG_VALUE.success;
                              }
                              if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
                                intraOpFlagValue = anesthesiaDataFlagValue;
                              } else {
                                if (isNoneIntraOpFormDocs) {
                                  intraOpFlagValue = FLAG_VALUE.none;
                                } else {
                                  if (intraOpFormDocs.length > 0) {
                                    intraOpFlagValue = FLAG_VALUE.success;
                                  } else {
                                    intraOpFlagValue = FLAG_VALUE.critical;
                                  }
                                }
                              }

                              // PostOp Tab Flag
                              let postOpFlagValue = FLAG_VALUE.critical;
                              if (isNonePostOpFormDocs) {
                                postOpFlagValue = FLAG_VALUE.none;
                              } else {
                                if (postOpFormDocs.length > 0) {
                                  postOpFlagValue = FLAG_VALUE.success;
                                } else {
                                  postOpFlagValue = FLAG_VALUE.critical;
                                }
                              }

                              // Procedures Tab Flag
                              let proceduresFlagValue = FLAG_VALUE.critical;
                              if (isNoneAnesthesiaProcedure) {
                                proceduresFlagValue = FLAG_VALUE.none;
                              } else {
                                if (anesthesiaProcedures.length === 0) {
                                  proceduresFlagValue = FLAG_VALUE.critical;
                                } else if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
                                  proceduresFlagValue = FLAG_VALUE.warning;
                                } else if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length === 0) {
                                  proceduresFlagValue = FLAG_VALUE.success;
                                } else {
                                  proceduresFlagValue = FLAG_VALUE.critical;
                                }
                              }

                              const anesthesiaTabFlag = findFlag(preOpFlagValue, intraOpFlagValue, postOpFlagValue, proceduresFlagValue);
                              switch (anesthesiaTabFlag) {
                                case FLAG_VALUE.critical:
                                  return CRITIAL_ICON;
                                case FLAG_VALUE.warning:
                                  return BELL_ICON;
                                case FLAG_VALUE.success:
                                  return SUCCESS_ICON;
                                case FLAG_VALUE.none:
                                  return IS_NONE_ICON;
                                default:
                                  return CRITIAL_ICON;
                              }
                            }
                            case 'Nursing':
                              if (isNoneNursingDocs) {
                                return IS_NONE_ICON;
                              }
                              if (nursingDocs.length > 0 || isNoneNursingDocs) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            case 'Surgeon':
                              if (isNoneSurgeonDocs) {
                                return IS_NONE_ICON;
                              }
                              if (surgeonDocs.length > 0 || isNoneSurgeonDocs) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            case 'Discharge':
                              if (isNoneDischargeDocs) {
                                return IS_NONE_ICON;
                              }
                              if (dischargeDocs.length > 0 || isNoneDischargeDocs) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            case 'Orders': {
                              if (isNoneOrdersPreOpDocs && isNoneOrdersIntraOpDocs && isNoneOrdersPostOpDocs) {
                                return IS_NONE_ICON;
                              }
                              const preOPFlag = ordersPreOpDocs.length > 0 || isNoneOrdersPreOpDocs;
                              const intraOpFlag = ordersIntraOpDocs.length > 0 || isNoneOrdersIntraOpDocs;
                              const postOpFlag = ordresPostOpDocs.length > 0 || isNoneOrdersPostOpDocs;

                              if (preOPFlag && intraOpFlag && postOpFlag) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            }
                            default:
                              return CRITIAL_ICON;
                          }
                        })()}
                        style={{
                          ...tab.styles,
                          background: activeTab === tab.text ? '#184878' : '#083050'
                        }}
                        textStyles={TABS_TEXT_STYLES}
                        onActivate={() => {
                          setActiveTab(tab.text);
                        }}
                      >
                        {!tab.isIconText ? tab.text : tab.textIcon}
                      </TabItem>
                    );
                  }
                } else {
                  return (
                    <TabItem
                      key={tab.id}
                      tabKey={tab.id}
                      parentKey={tab.pKey}
                      text={tab.text}
                      isActive={activeTab === tab.text}
                      showTabIcon
                      showSvgIcon
                      svgIcon={(() => {
                        switch (tab.text) {
                          case 'FaceSheet':
                            if (isNoneFaceSheet) {
                              return <DocumentNoneIcon height={20} />
                            } else {
                              if (faceSheetDocs && Array.isArray(faceSheetDocs)) {
                                return faceSheetDocs.length > 0
                                  ? <DocumentSuccessIcon height={20} />
                                  : <DocumentCriticalIcon height={20} />
                              }
                              return <DocumentCriticalIcon height={20} />;
                            }
                          case 'Records': {
                            if (isNoneLabsDocs && isNoneEkgDocs && isNoneImagingDocs && isNoneConsults) {
                              return <DocumentNoneIcon height={20} />;
                            }

                            const labsFlag = (labsDocs && Array.isArray(labsDocs) && labsDocs.length > 0) || isNoneLabsDocs;
                            const ekgFlag = (ekgDocs && Array.isArray(ekgDocs) && ekgDocs.length > 0) || isNoneEkgDocs;
                            const imagingFlag = (imagingDocs && Array.isArray(imagingDocs) && imagingDocs.length > 0) || isNoneImagingDocs;
                            const consultsFlag = (consultsDocs && Array.isArray(consultsDocs) && consultsDocs.length > 0) || isNoneConsults;

                            if (labsFlag && ekgFlag && imagingFlag && consultsFlag) {
                              return <DocumentSuccessIcon height={20} />;
                            }
                            return <DocumentCriticalIcon height={20} />;
                          }
                          case 'Consents': {
                            if (isNoneConsentSurgFormDocs && isNoneConsentFormDocs) {
                              return <DocumentNoneIcon height={20} />;
                            }

                            const surgFlag = (consentSurgFormDocs && Array.isArray(consentSurgFormDocs) && consentSurgFormDocs.length > 0) || isNoneConsentSurgFormDocs;
                            const anesFlag = (consentFormDocs && Array.isArray(consentFormDocs) && consentFormDocs.length > 0) || isNoneConsentFormDocs;

                            if (surgFlag && anesFlag) {
                              return <DocumentSuccessIcon height={20} />;
                            }
                            return <DocumentCriticalIcon height={20} />;
                          }
                          case 'Anesthesia': {
                            // PreOp Tab Flag
                            let preOpFlagValue = FLAG_VALUE.critical;
                            if (isNonePreOpFormDocs) {
                              preOpFlagValue = FLAG_VALUE.none;
                            } else {
                              if (preOpFormDocs && Array.isArray(preOpFormDocs) && preOpFormDocs.length > 0) {
                                preOpFlagValue = FLAG_VALUE.success;
                              } else {
                                preOpFlagValue = FLAG_VALUE.critical;
                              }
                            }

                            // IntraOp Tab Flag
                            let intraOpFlagValue = FLAG_VALUE.critical;
                            let anesthesiaDataFlagValue = FLAG_VALUE.critical;
                            if (asaScore.length > 0 && anesthesiaTypes.length > 0 && anesthesiaModifiers.length > 0) {
                              anesthesiaDataFlagValue = FLAG_VALUE.success;
                            }
                            if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
                              intraOpFlagValue = anesthesiaDataFlagValue;
                            } else {
                              if (isNoneIntraOpFormDocs) {
                                intraOpFlagValue = FLAG_VALUE.none;
                              } else {
                                if (intraOpFormDocs.length > 0) {
                                  intraOpFlagValue = FLAG_VALUE.success;
                                } else {
                                  intraOpFlagValue = FLAG_VALUE.critical;
                                }
                              }
                            }

                            // PostOp Tab Flag
                            let postOpFlagValue = FLAG_VALUE.critical;
                            if (isNonePostOpFormDocs) {
                              postOpFlagValue = FLAG_VALUE.none;
                            } else {
                              if (postOpFormDocs && Array.isArray(postOpFormDocs) && postOpFormDocs.length > 0) {
                                postOpFlagValue = FLAG_VALUE.success;
                              } else {
                                postOpFlagValue = FLAG_VALUE.critical;
                              }
                            }

                            // Procedures Tab Flag
                            let proceduresFlagValue = FLAG_VALUE.critical;
                            if (isNoneAnesthesiaProcedure) {
                              proceduresFlagValue = FLAG_VALUE.none;
                            } else {
                              if (anesthesiaProcedures.length === 0) {
                                proceduresFlagValue = FLAG_VALUE.critical;
                              } else if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
                                proceduresFlagValue = FLAG_VALUE.warning;
                              } else if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length === 0) {
                                proceduresFlagValue = FLAG_VALUE.success;
                              } else {
                                proceduresFlagValue = FLAG_VALUE.critical;
                              }
                            }

                            const anesthesiaTabFlag = findFlag(preOpFlagValue, intraOpFlagValue, postOpFlagValue, proceduresFlagValue);
                            switch (anesthesiaTabFlag) {
                              case FLAG_VALUE.critical:
                                return <DocumentCriticalIcon height={20} />;
                              case FLAG_VALUE.warning:
                                return <DocumentWarningIcon height={20} />;
                              case FLAG_VALUE.success:
                                return <DocumentSuccessIcon height={20} />;
                              case FLAG_VALUE.none:
                                return <DocumentNoneIcon height={20} />;
                              default:
                                return <DocumentCriticalIcon height={20} />;
                            }
                          }
                          case 'Nursing':
                            if (isNoneNursingDocs) {
                              return <DocumentNoneIcon height={20} />;
                            }
                            if ((nursingDocs && Array.isArray(nursingDocs) && nursingDocs.length > 0) || isNoneNursingDocs) {
                              return <DocumentSuccessIcon height={20} />;
                            }
                            return <DocumentCriticalIcon height={20} />;
                          case 'Surgeon':
                            if (isNoneSurgeonDocs) {
                              return <DocumentNoneIcon height={20} />;
                            }
                            if ((surgeonDocs && Array.isArray(surgeonDocs) && surgeonDocs.length > 0) || isNoneSurgeonDocs) {
                              return <DocumentSuccessIcon height={20} />;
                            }
                            return <DocumentCriticalIcon height={20} />;
                          case 'Discharge':
                            if (isNoneDischargeDocs) {
                              return <DocumentNoneIcon height={20} />;
                            }
                            if ((dischargeDocs && Array.isArray(dischargeDocs) && dischargeDocs.length > 0) || isNoneDischargeDocs) {
                              return <DocumentSuccessIcon height={20} />;
                            }
                            return <DocumentCriticalIcon height={20} />;
                          case 'Orders': {
                            if (isNoneOrdersPreOpDocs && isNoneOrdersIntraOpDocs && isNoneOrdersPostOpDocs) {
                              return <DocumentNoneIcon height={20} />;
                            }
                            const preOPFlag = (ordersPreOpDocs && Array.isArray(ordersPreOpDocs) && ordersPreOpDocs.length > 0) || isNoneOrdersPreOpDocs;
                            const intraOpFlag = (ordersIntraOpDocs && Array.isArray(ordersIntraOpDocs) && ordersIntraOpDocs.length > 0) || isNoneOrdersIntraOpDocs;
                            const postOpFlag = (ordresPostOpDocs && Array.isArray(ordresPostOpDocs) && ordresPostOpDocs.length > 0) || isNoneOrdersPostOpDocs;

                            if (preOPFlag && intraOpFlag && postOpFlag) {
                              return <DocumentSuccessIcon height={20} />;
                            }
                            return <DocumentCriticalIcon height={20} />;
                          }
                          default:
                            return <DocumentCriticalIcon height={20} />;
                        }
                      })()}
                      style={{
                        ...tab.styles,
                        background: activeTab === tab.text ? '#184878' : '#083050'
                      }}
                      textStyles={TABS_TEXT_STYLES}
                      onActivate={() => {
                        setActiveTab(tab.text);
                      }}
                    >
                      {!tab.isIconText ? tab.text : tab.textIcon}
                    </TabItem>
                  );
                }
              })}
            </TabsContainer>
          </div>

          {/* Active Component - Doc Container */}
          <div className={css(styles.docContainer)}>
            {activeDocComponent}
          </div>
        </div>
        {/* --- Schedule Drawer -------- */}
        <div
          className={css(styles.scheduleFormContainer,
            isMobileLayout && styles.scheduleFormContainerMobile,
            !showScheduleDrawer && styles.scheduleFormContainerClosed
          )}
        >
          {/* Header */}
          <div className={css(styles.scheduleFormHeader)}>
            {/* Header Text */}
            <div className={css(styles.scheduleFormHeaderTitle)}>
              {masterFlag === FLAG_VALUE.success && <CaseSuccessIcon height={18} />}
              {masterFlag === FLAG_VALUE.warning && <CaseWarningIcon height={18} />}
              {masterFlag === FLAG_VALUE.critical && <CaseCriticalIcon height={18} />}
              <span className='ml-2'>
                Case Posting
              </span>
            </div>
            {/* Close Icon */}
            <div className={css(styles.scheduleFormHeaderCloseIcon)}>
              <i
                className='fa fa-times-circle'
                onClick={closeScheduleCaseDrawer}
              />
            </div>
          </div>
          {/* Schedule Drawer Tabs */}
          <div className={css(styles.scheduleDrawerTabs)}>
            <TabsContainer
              dropDownId="new_case_post_drawer_tabs"
              showPlusIcon={false}
              isDrawer={false}
              shiftMethod='minWidth'
              minWidth={610}
              containerStyles={{ width: '100%' }}
            >
              {/* Case Tab */}
              <TabItem
                key='1211'
                tabKey='1211'
                parentKey='1200'
                text='Case'
                isActive={drawerActiveTab === 'case'}
                showTabIcon
                showSvgIcon
                // svgIcon={caseTabIcon}
                svgIcon={(() => {
                  switch (props.caseTabFlag) {
                    case FLAG_VALUE.success:
                      return <CaseSuccessIcon height={18} />;
                    case FLAG_VALUE.warning:
                      return <CaseWarningIcon height={18} />;
                    case FLAG_VALUE.critical:
                    default:
                      return <CaseCriticalIcon height={18} />;
                  }
                })()}
                style={{
                  ...COMMON_TAB_ITEM_STYLES,
                  width: '105px',
                  minWidth: '105px',
                  maxWidth: '105px',
                  background: drawerActiveTab === 'case' ? '#184878' : '#083050'
                }}
                textStyles={TABS_TEXT_STYLES}
                onActivate={() => {
                  handleOnActivateDrawerTab('case');
                }}
              >
                Case
              </TabItem>
              {/* Patient Tab */}
              <TabItem
                key='1212'
                tabKey='1212'
                parentKey='1201'
                text='Patient'
                isActive={drawerActiveTab === 'patient'}
                showTabIcon
                showSvgIcon
                svgIcon={patientFlag === FLAG_VALUE.success
                  ? <CaseSuccessIcon height={18} />
                  : <CaseWarningIcon height={18} />
                }
                style={{
                  ...COMMON_TAB_ITEM_STYLES,
                  width: '105px',
                  minWidth: '105px',
                  maxWidth: '105px',
                  background: drawerActiveTab === 'patient' ? '#184878' : '#083050'
                }}
                textStyles={TABS_TEXT_STYLES}
                onActivate={() => {
                  handleOnActivateDrawerTab('patient');
                }}
              >
                Patient
              </TabItem>
              {/* Payers Tab */}
              <TabItem
                key='1213'
                tabKey='1214'
                parentKey='1301'
                text='Payers'
                isActive={drawerActiveTab === 'payers'}
                showTabIcon
                showSvgIcon
                svgIcon={payerFlag === FLAG_VALUE.success
                  ? <CaseSuccessIcon height={18} />
                  : <CaseWarningIcon height={18} />
                }
                style={{
                  ...COMMON_TAB_ITEM_STYLES,
                  width: '105px',
                  minWidth: '105px',
                  maxWidth: '105px',
                  background: drawerActiveTab === 'payers' ? '#184878' : '#083050'
                }}
                textStyles={TABS_TEXT_STYLES}
                onActivate={() => {
                  handleOnActivateDrawerTab('payers');
                }}
              >
                Payers
              </TabItem>
            </TabsContainer>
          </div>
          {/* Active Component */}
          <div className={css(styles.activeComponentContainer)}>
            {activeDrawerComponent}
          </div>
        </div>
      </div>
    </div>
  );
};

// export default CbScheduleDashboard;
function mapStateToProps({ caseSchedule }) {
  return { ...caseSchedule };
}

export default connect(mapStateToProps, {
  updateCaseBlockActiveCaseSchedule,
  markCaseForUpdate,
  addPatientToCaseBlock,
  setShouldSaveCaseData
})(CbScheduleDashboard);
